import { Select, Spin, Tabs } from "antd";
import { invert } from "lodash";
import { ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { ApiCallStatusV2 } from "src/common/ApiCall";
import { Nullable } from "src/common/utils/nullable";
import { RootState } from "src/redux/store";
import "./index.scss";
import { AccessVisibilityActions } from "./model/model";
import { SystemResourceType, SystemResourceTypes } from "./model/systemResourceTypes";
import { AccessVisibilityBasedOnInformationAssets } from "./tabs/AccessVisibilityBasedOnInformationAssets";
import { AccessVisibilityBasedOnSystemResource } from "./tabs/AccessVisibilityBasedOnSystemResource";
import { IsFeatureEnabledOrNot } from "src/FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "src/FeatureToggles/toggleList";

export enum AccessVisibilityBasedOn {
  INFORMATION_ASSETS = "INFORMATION_ASSETS",
  SYSTEM_RESOURCES = "SYSTEM_RESOURCES",
}

export const AccessVisibilityRoutesVsTabKeyMap = {
  "information-assets": "INFORMATION_ASSETS",
  "system-resources": "SYSTEM_RESOURCES",
};

export const AccessVisibilityTabKeyVsRoutesMap = invert(AccessVisibilityRoutesVsTabKeyMap);

export function AccessVisibility(props: {
  userEmail: Nullable<string>;
  userGroup: Nullable<{
    id: string;
    type: string;
  }>;
  toShowFor: { user: boolean; userGroup: boolean };
  reduceTableHeight: string | null;
}): ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    params: { subTabName: tabName, systemResourceType: systemResourceTypeIdQueryParam },
  } = useRouteMatch<{ subTabName: string; systemResourceType: string | undefined }>();
  const accessVisibility = useSelector((state: RootState) => state.accessVisibility);
  const systemResourceTypesApiCallStatus = new ApiCallStatusV2(
    accessVisibility.systemResourceTypes.progress,
    accessVisibility.systemResourceTypes.error
  );
  
  const isAccessVisibilityForSisenseDatasourceFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.AccessVisibilityForSisenseDatasource)
  const systemResourceTypes = new SystemResourceTypes(accessVisibility.systemResourceTypes.value);
  const [filteredSystemResourceType, setFilteredSystemResourceType] =
    useState<SystemResourceType | null>(selectSystemResourceTypeFilter());
  const [activeKey, setActiveKey] = useState<string | undefined>(
    AccessVisibilityBasedOn.SYSTEM_RESOURCES
  );

  function selectSystemResourceTypeFilter(): Nullable<SystemResourceType> {
    if (isAccessVisibilityForSisenseDatasourceFeatureEnabled && systemResourceTypeIdQueryParam) {
      return systemResourceTypes.getById(systemResourceTypeIdQueryParam);
    } else {
      return systemResourceTypes.getByName("Sisense Dashboard");
    }
  }

  useEffect(() => {
    if (!systemResourceTypesApiCallStatus.isCompletedSuccessfully) {
      dispatch(AccessVisibilityActions.getSystemResourceTypes());
    }
  }, []);

  useEffect(() => {
    if (tabName) {
      setActiveKey(AccessVisibilityRoutesVsTabKeyMap[tabName]);
    }
  }, [tabName]);

  useEffect(() => {
    if (systemResourceTypeIdQueryParam) {
      setFilteredSystemResourceType(selectSystemResourceTypeFilter());
    }
  }, [JSON.stringify(systemResourceTypes.ids()), systemResourceTypeIdQueryParam]);
  
  useEffect(() => {
      setFilteredSystemResourceType(selectSystemResourceTypeFilter());
  }, [JSON.stringify(systemResourceTypes.ids())]);

  function getAccessVisibilityUrl(selectedSystemResourceType: SystemResourceType, activeTab: string) {
    const currentUrl = window.location.pathname.replace(/\/$/, "");
    const baseUrl = currentUrl.split("/access-visibility")[0];
    return `${baseUrl}/access-visibility/${selectedSystemResourceType.id}/${AccessVisibilityTabKeyVsRoutesMap[activeTab]}`;
  }

  function updateUrlOnSystemResourceTypeChange(selectedSystemResourceType:SystemResourceType) {
    history.push(getAccessVisibilityUrl(selectedSystemResourceType, activeKey!!));
  }

  const handleNavigate = (tabKey: string) => {
    history.push(getAccessVisibilityUrl(filteredSystemResourceType!!, tabKey));
  };

  return (
    <div className="current-access-controls-container">
      {systemResourceTypesApiCallStatus.isLoading ? (
        <div className="spin-container">
          <Spin spinning={true} />
        </div>
      ) : systemResourceTypesApiCallStatus.isFailed ? (
        <div className="error-text">Error loading details</div>
      ) : (
        <div className="current-access-controls-content">
          {filteredSystemResourceType === null ? (
            <div>System Resource Type is not selected.</div>
          ) : (
            <Tabs
              type={"line"}
              tabBarExtraContent={{
                right: (
                  <div className="filter-system-resource-types">
                    <Select
                      value={filteredSystemResourceType.name}
                      defaultValue={filteredSystemResourceType.name}
                      options={systemResourceTypes.options(
                        props.toShowFor.user,
                        isAccessVisibilityForSisenseDatasourceFeatureEnabled
                      )}
                      onChange={(value) => {
                        const selectedSystemResourceType = systemResourceTypes.getByName(value);
                        setFilteredSystemResourceType(selectedSystemResourceType);
                        updateUrlOnSystemResourceTypeChange(selectedSystemResourceType!!);
                      }}
                    />
                  </div>
                ),
              }}
              animated={{ inkBar: false, tabPane: false }}
              defaultActiveKey={AccessVisibilityBasedOn.SYSTEM_RESOURCES}
              activeKey={activeKey}
              onChange={(key) => handleNavigate(key)}
              destroyInactiveTabPane={true}
            >
              <Tabs.TabPane tab="System Resources" key={AccessVisibilityBasedOn.SYSTEM_RESOURCES}>
                <div style={{ margin: "1rem" }} />
                <AccessVisibilityBasedOnSystemResource
                  userEmail={props.userEmail}
                  filteredSystemResourceType={filteredSystemResourceType!!}
                  toShowFor={props.toShowFor}
                  userGroup={props.userGroup}
                  reduceTableHeight={props.reduceTableHeight}
                  isActive={activeKey === AccessVisibilityBasedOn.SYSTEM_RESOURCES}
                />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab="Information Assets"
                key={AccessVisibilityBasedOn.INFORMATION_ASSETS}
              >
                <div style={{ margin: "1rem" }} />
                <AccessVisibilityBasedOnInformationAssets
                  userEmail={props.userEmail}
                  filteredSystemResourceType={filteredSystemResourceType!!}
                  toShowFor={props.toShowFor}
                  userGroup={props.userGroup}
                  reduceTableHeight={props.reduceTableHeight}
                  isActive={activeKey === AccessVisibilityBasedOn.INFORMATION_ASSETS}
                />
              </Tabs.TabPane>
            </Tabs>
          )}
        </div>
      )}
    </div>
  );
}
